import React, { useState, ReactNode } from "react";
import { cn } from "../../styles";
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  DialogActions,
} from "../../layouts/Dialog";
import { TabBar } from "../../layouts/TabBar";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyContent,
} from "../../atoms";
import { ThemeColor } from "../../props";
import { Badge } from "../../atoms/Badge";
import { LocalizationScope } from "../../util/LocalizationScope";

export interface SelectionDialogTab {
  label: TypographyContent;
  badge?: number | boolean;
  icon?: string;
  localKey?: string;
  /** Custom render function for tab con ptent (e.g. portal-backed tabs) */
  render?: (ctx: {
    isActive: boolean;
    onSelect: SelectionDialogProps["onSelect"];
  }) => ReactNode;
  /** Inline form fields for this tab */
  fields?: any[];
  /** Domain identifier for the tab */
  domain?: string;
  /** Whether the submit button is shown for this tab */
  showSubmit?: boolean;
}

export interface SelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (values: any, domain?: string, isSingleMatch?: boolean) => void;
  onClear?: (() => void) | false | null;
  tabs: SelectionDialogTab[];
  localKey?: string;
  hint?: TypographyContent;
  /** Render prop for the form tab content */
  renderFormTab?: (tab: SelectionDialogTab, index: number) => ReactNode;
  /** Render prop for the submit button */
  renderSubmit?: (tab: SelectionDialogTab, index: number) => ReactNode;
  /** Custom dialog width */
  width?: string | number;
  /** Custom dialog height */
  height?: string | number;
  className?: string;
}

export const SelectionDialog = ({
  open,
  onClose,
  onSelect,
  onClear,
  tabs,
  localKey,
  hint,
  renderFormTab,
  renderSubmit,
  width = "70vw",
  height = "80vh",
  className,
}: SelectionDialogProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabBarTabs = tabs.map((tab, idx) => ({
    label: (
      <LocalizationScope scope={tab.localKey}>
        <div className="px-5 py-0.5">
          {tab.badge !== undefined ? (
            <Badge
              content={tab.badge === true ? 0 : tab.badge}
              color={ThemeColor.primary}
            >
              <Typography.Span content={tab.label} />
            </Badge>
          ) : (
            <Typography.Span content={tab.label} />
          )}
        </div>
      </LocalizationScope>
    ),
    value: String(idx),
  }));

  const currentTab = tabs[activeTab];

  return (
    <LocalizationScope scope={["", localKey, "::PSS::SelectionDialog"]}>
      <Dialog
        open={open}
        onClose={onClose}
        minWidth={width}
        height={height}
        className={className}
      >
        {tabs.length > 1 && (
          <DialogHeader className="px-0 py-0">
            <TabBar
              tabs={tabBarTabs}
              value={String(activeTab)}
              onChange={(v) => setActiveTab(Number(v))}
              fullWidth
              className="w-full"
            />
          </DialogHeader>
        )}
        <DialogContent separated className="p-0">
          {tabs.map((tab, idx) => {
            const isActive = idx === activeTab;
            if (!isActive) return null;

            if (tab.render) {
              return (
                <LocalizationScope key={idx} scope={tab.localKey}>
                  <div className="flex flex-col h-full">
                    {tab.render({ isActive, onSelect })}
                  </div>
                </LocalizationScope>
              );
            }

            if (renderFormTab) {
              return (
                <LocalizationScope key={idx} scope={tab.localKey}>
                  {renderFormTab(tab, idx)}
                </LocalizationScope>
              );
            }

            return null;
          })}
        </DialogContent>
        <DialogFooter>
          {hint && (
            <Typography.P
              className="self-center text-[0.6rem] text-front-400"
              content={hint}
            />
          )}
          <DialogActions>
            <Button variant={ButtonVariant.text} onClick={onClose}>
              <Typography.Span content={{ local: "Cancel" }} />
            </Button>
            {onClear && (
              <Button variant={ButtonVariant.text} onClick={onClear}>
                <Typography.Span content={{ local: "Clear" }} />
              </Button>
            )}
            {renderSubmit && renderSubmit(currentTab, activeTab)}
          </DialogActions>
        </DialogFooter>
      </Dialog>
    </LocalizationScope>
  );
};

SelectionDialog.displayName = "SelectionDialog";
